import {
    AreaChart,
    Area,
    ResponsiveContainer,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import {
    Eye,
    Award,
    Hexagon,
    Layers,
    SplitSquareHorizontal,
    Activity,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const perfTrend = [
    { m: "Opp. 1", v: 2 },
    { m: "Opp. 2", v: 5 },
    { m: "Opp. 3", v: 1.5 },
    { m: "Opp. 4", v: 0.8 },
    { m: "Opp. 5", v: 9 },
    { m: "Opp. 6", v: 5 },
    { m: "Opp. 7", v: 3 },
    { m: "Opp. 8", v: -11 },
    { m: "Opp. 9", v: 6 },
    { m: "Opp. 10", v: 9 },
];

const radarData = [
    { axis: "SWP", v: 57 },
    { axis: "RWP", v: 38 },
    { axis: "WM", v: 50 },
    { axis: "WP", v: 50 },
    { axis: "RA", v: 45 },
    { axis: "RM", v: 50 },
];

const fhData = [
    { name: "FH winners", value: 267, fill: "#D4FF00" },
    { name: "FH errors", value: 122, fill: "#7BAA00" },
    { name: "FH neutral", value: 81, fill: "#3F5A00" },
];
const bhData = [
    { name: "BH winners", value: 142, fill: "#A8DD9A" },
    { name: "BH errors", value: 61, fill: "#5E8A55" },
    { name: "BH neutral", value: 145, fill: "#2F4D29" },
];

const FEATURE_ICONS = [Hexagon, Award, Activity, Layers, SplitSquareHorizontal, Eye];

export default function Dashboard() {
    const { t } = useI18n();
    const d = t.dashboard;
    return (
        <section
            id="dashboard"
            data-testid="dashboard-section"
            className="relative py-24 md:py-32 border-t border-white/5"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-8 mb-12 reveal">
                    <div className="lg:col-span-7">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-4">
                            {d.tag}
                        </div>
                        <h2 className="font-display font-bold uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter heading-clamp">
                            {d.headline}{" "}
                            <span className="text-[#D4FF00]">{d.headlineAccent}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 self-end">
                        <p className="text-white/65 text-lg leading-relaxed">{d.sub}</p>
                    </div>
                </div>

                <div
                    data-testid="dashboard-mockup"
                    className="rounded-3xl border border-white/10 bg-[#071B12] overflow-hidden shadow-[0_60px_120px_-30px_rgba(0,0,0,0.8)] reveal"
                >
                    <div className="flex items-center justify-between px-5 md:px-7 py-4 border-b border-white/5 bg-[#04140D]/60">
                        <div className="flex items-center gap-3">
                            <div className="flex gap-1.5">
                                <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                                <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[#D4FF00]" />
                            </div>
                            <div className="text-xs text-white/55 font-mono">
                                davini.ai / players / sample-itf-junior
                            </div>
                        </div>
                        <div className="text-[10px] tracking-widest uppercase text-white/40 font-semibold">
                            ITF junior · Sample data
                        </div>
                    </div>

                    <div className="px-5 md:px-7 py-6 border-b border-white/5">
                        <div className="flex flex-wrap items-end gap-5 justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-[#D4FF00] font-bold">
                                    {d.playerLabel}
                                </div>
                                <div className="font-display font-bold uppercase text-3xl md:text-4xl tracking-tight mt-1">
                                    ITF Junior · #IT-2026-A
                                </div>
                                <div className="text-sm text-white/55 mt-1">{d.playerSub}</div>
                            </div>
                            <div className="flex gap-2">
                                {[
                                    "PERFORMANCE",
                                    "P1",
                                    "M4",
                                    "RALLY",
                                    "SERVE",
                                    "S.E.M.",
                                    "RETURN",
                                    "R.E.M.",
                                ].map((t2, i) => (
                                    <span
                                        key={t2}
                                        className={`px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest border ${
                                            i === 0
                                                ? "bg-[#D4FF00] text-[#04140D] border-[#D4FF00]"
                                                : "border-white/10 text-white/55"
                                        }`}
                                    >
                                        {t2}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-5 p-5 md:p-7">
                        <div className="lg:col-span-7 rounded-2xl border border-white/5 bg-[#04140D]/60 p-5">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] tracking-widest uppercase font-bold text-[#D4FF00]">
                                    {d.perfTitle}
                                </span>
                                <span className="text-[10px] text-white/40 font-mono">
                                    {d.perfMargin}
                                </span>
                            </div>
                            <div className="h-[220px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={perfTrend}>
                                        <defs>
                                            <linearGradient id="perfFill" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#D4FF00" stopOpacity={0.5} />
                                                <stop offset="100%" stopColor="#D4FF00" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                                        <XAxis
                                            dataKey="m"
                                            tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                background: "#04140D",
                                                border: "1px solid rgba(255,255,255,0.1)",
                                                borderRadius: 8,
                                                fontSize: 12,
                                            }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="v"
                                            stroke="#D4FF00"
                                            strokeWidth={2.5}
                                            fill="url(#perfFill)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="lg:col-span-5 rounded-2xl border border-white/5 bg-[#04140D]/60 p-5">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] tracking-widest uppercase font-bold text-[#D4FF00]">
                                    {d.skillsetTitle}
                                </span>
                                <span className="text-[10px] text-white/40 font-mono">
                                    {d.skillsetSub}
                                </span>
                            </div>
                            <div className="h-[220px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadarChart data={radarData}>
                                        <PolarGrid stroke="rgba(255,255,255,0.1)" />
                                        <PolarAngleAxis
                                            dataKey="axis"
                                            tick={{
                                                fill: "rgba(255,255,255,0.6)",
                                                fontSize: 11,
                                                fontWeight: 700,
                                            }}
                                        />
                                        <Radar
                                            dataKey="v"
                                            stroke="#D4FF00"
                                            strokeWidth={2}
                                            fill="#D4FF00"
                                            fillOpacity={0.25}
                                        />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="lg:col-span-5 rounded-2xl border border-white/5 bg-[#04140D]/60 p-5">
                            <div className="text-[10px] tracking-widest uppercase font-bold text-[#D4FF00] mb-2">
                                {d.fhbhTitle}
                            </div>
                            <div className="h-[220px] grid grid-cols-2">
                                {[fhData, bhData].map((dataset, idx) => (
                                    <div key={idx} className="relative">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={dataset}
                                                    innerRadius={42}
                                                    outerRadius={70}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                >
                                                    {dataset.map((dd, i) => (
                                                        <Cell key={i} fill={dd.fill} />
                                                    ))}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <div className="text-[10px] uppercase tracking-widest text-white/50">
                                                {idx === 0 ? d.forehand : d.backhand}
                                            </div>
                                            <div className="font-display text-xl font-bold text-white tabular">
                                                {dataset.reduce((s, x) => s + x.value, 0)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="lg:col-span-4 rounded-2xl border border-white/5 bg-[#04140D]/60 p-5">
                            <div className="text-[10px] tracking-widest uppercase font-bold text-[#D4FF00] mb-3">
                                {d.minimatchTitle}
                            </div>
                            <div className="space-y-3">
                                {[
                                    ["1st Serve", 38.77, "#22D3EE"],
                                    ["2nd Serve", 50.23, "#D4FF00"],
                                    ["1st Return", 36.92, "#A78BFA"],
                                    ["2nd Return", 51.08, "#F59E0B"],
                                ].map(([k, v, c]) => (
                                    <div key={k}>
                                        <div className="flex justify-between text-[11px] mb-1">
                                            <span className="text-white/70 font-semibold">{k}</span>
                                            <span className="tabular text-white font-bold">{v}%</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-[#0E2B1E] overflow-hidden">
                                            <div
                                                className="h-full rounded-full"
                                                style={{ width: `${v}%`, background: c }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="lg:col-span-3 rounded-2xl border border-white/5 bg-[#04140D]/60 p-5">
                            <div className="text-[10px] tracking-widest uppercase font-bold text-[#D4FF00] mb-2">
                                {d.qualityTitle}
                            </div>
                            <ol className="space-y-2 text-sm">
                                {[
                                    "vs. Opp. 6",
                                    "vs. Opp. 3",
                                    "vs. Opp. 9",
                                    "vs. Opp. 5",
                                    "vs. Opp. 2",
                                ].map((t2, i) => (
                                    <li
                                        key={i}
                                        className="flex items-center justify-between text-white/75 border-b border-white/5 pb-2 last:border-0"
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className="text-[#D4FF00] font-bold tabular w-5">
                                                #{i + 1}
                                            </span>
                                            <span className="text-xs">{t2}</span>
                                        </span>
                                        <Eye size={14} className="text-white/35" />
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-10 reveal">
                    {d.featurePills.map((label, i) => {
                        const Icon = FEATURE_ICONS[i];
                        return (
                            <div
                                key={label}
                                data-testid={`feature-${i}`}
                                className="card-hover rounded-xl border border-white/10 bg-[#071B12] p-4 flex items-center gap-3"
                            >
                                <div className="h-9 w-9 rounded-md bg-[#04140D] border border-white/10 grid place-items-center text-[#D4FF00]">
                                    <Icon size={16} />
                                </div>
                                <span className="text-xs font-semibold text-white/85 leading-tight">
                                    {label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
